import { useOnboarding } from "@onboardjs/react";
import { useMutation, useQuery } from "convex/react";
import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { api } from "../../../convex/_generated/api";
import { Button } from "../../components/ui/button";
import { MarkdownEditor } from "../../components/ui/markdown-editor";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../../components/ui/tabs";

function stripCodeFences(text: string): string {
	return text
		.replace(/^```(?:json)?\s*\n?/i, "")
		.replace(/\n?```\s*$/i, "")
		.trim();
}

function tryParseJson(
	text: string | undefined,
): Record<string, unknown> | null {
	if (!text) return null;
	try {
		return JSON.parse(stripCodeFences(text));
	} catch {
		return null;
	}
}

function buildFirmProfileMarkdown(
	firmData: Record<string, unknown>,
	teamData: unknown[] | null,
): string {
	const lines: string[] = [];

	lines.push("## Firm");
	if (firmData.firmName) lines.push(`Name: ${firmData.firmName}`);
	if (firmData.address) lines.push(`Address: ${firmData.address}`);
	if (firmData.phone) lines.push(`Phone: ${firmData.phone}`);
	if (firmData.fax) lines.push(`Fax: ${firmData.fax}`);
	if (firmData.email) lines.push(`Email: ${firmData.email}`);
	if (firmData.website) lines.push(`Website: ${firmData.website}`);
	if (firmData.timezone) lines.push(`Timezone: ${firmData.timezone}`);

	if (firmData.description) {
		lines.push("");
		lines.push(String(firmData.description));
	}

	if (
		Array.isArray(firmData.practiceAreas) &&
		firmData.practiceAreas.length > 0
	) {
		lines.push("");
		lines.push("## Practice Areas");
		for (const area of firmData.practiceAreas) {
			const a = area as { name?: string; description?: string };
			lines.push(
				`- ${a.name ?? ""}${a.description ? ` — ${a.description}` : ""}`,
			);
		}
	}

	if (teamData && teamData.length > 0) {
		lines.push("");
		lines.push("## People");
		lines.push("| Name | Role | Email |");
		lines.push("|------|------|-------|");
		for (const member of teamData) {
			const m = member as {
				name?: string;
				role?: string;
				email?: string;
				isRetired?: boolean;
			};
			if (m.isRetired) continue;
			lines.push(`| ${m.name ?? ""} | ${m.role ?? ""} | ${m.email ?? ""} |`);
		}
	}

	lines.push("");
	lines.push("## Routing");
	lines.push(
		"Route general inquiries to the managing attorney. Route case-specific questions to the assigned attorney.",
	);

	return lines.join("\n");
}

const EMPTY_FIRM =
	"## Firm\nName: \nAddress: \nPhone: \nWebsite: \n\n## Practice Areas\n- \n\n## People\n| Name | Role | Email |\n|------|------|-------|\n\n## Routing\n";

const EMPTY_GUIDE =
	"## Tone & Voice\n\n## Salutations\n\n## Closings\n\n## Email Rules\n\n## Signature Blocks\n\n## Disclaimers\n";

export function ReviewFirmStep() {
	const { next } = useOnboarding();
	const discovery = useQuery(api.codex.queries.getDiscovery);
	const saveFirmProfile = useMutation(api.codex.mutations.saveFirmProfile);
	const saveCommunicationGuide = useMutation(
		api.codex.mutations.saveCommunicationGuide,
	);

	const [saving, setSaving] = useState(false);
	// Parse extracted data from discovery
	const firmData = useMemo(
		() => tryParseJson(discovery?.extractedFirm ?? undefined),
		[discovery?.extractedFirm],
	);
	const teamData = useMemo(() => {
		if (!discovery?.extractedTeam) return null;
		try {
			const parsed = JSON.parse(stripCodeFences(discovery.extractedTeam));
			return Array.isArray(parsed) ? parsed : null;
		} catch {
			return null;
		}
	}, [discovery?.extractedTeam]);

	// Build content from extracted data
	const generatedFirmContent = useMemo(
		() =>
			firmData ? buildFirmProfileMarkdown(firmData, teamData) : EMPTY_FIRM,
		[firmData, teamData],
	);
	const generatedGuideContent = useMemo(
		() =>
			typeof firmData?.communicationGuide === "string"
				? firmData.communicationGuide
				: EMPTY_GUIDE,
		[firmData?.communicationGuide],
	);

	// Track user edits separately from generated content
	const [firmContent, setFirmContent] = useState<string | null>(null);
	const [guideContent, setGuideContent] = useState<string | null>(null);

	const effectiveFirmContent = firmContent ?? generatedFirmContent;
	const effectiveGuideContent = guideContent ?? generatedGuideContent;

	const handleSave = async () => {
		setSaving(true);
		try {
			await saveFirmProfile({
				name: "Firm Profile",
				content: effectiveFirmContent,
			});
			await saveCommunicationGuide({
				name: "Communication Guide",
				content: effectiveGuideContent,
			});
			await next();
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="flex flex-col gap-4">
			<Tabs defaultValue="firm">
				<TabsList>
					<TabsTrigger value="firm">Firm Profile</TabsTrigger>
					<TabsTrigger value="guide">Communication Guide</TabsTrigger>
				</TabsList>

				<TabsContent value="firm">
					<div className="flex flex-col gap-2">
						<p className="text-sm text-muted-foreground">
							This profile tells Clerical about your firm — its name, practice
							areas, team members, and how to route inquiries.
						</p>
						<MarkdownEditor
							value={effectiveFirmContent}
							onChange={(md) => setFirmContent(md)}
							placeholder="Enter your firm profile..."
						/>
					</div>
				</TabsContent>

				<TabsContent value="guide">
					<div className="flex flex-col gap-2">
						<p className="text-sm text-muted-foreground">
							This guide defines how Clerical communicates on behalf of your
							firm — tone, email rules, signature blocks, and disclaimers.
						</p>
						<MarkdownEditor
							value={effectiveGuideContent}
							onChange={(md) => setGuideContent(md)}
							placeholder="Enter your communication guide..."
						/>
					</div>
				</TabsContent>
			</Tabs>

			<div className="flex justify-end pt-2">
				<Button onClick={handleSave} disabled={saving}>
					{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
					Save & Continue
				</Button>
			</div>
		</div>
	);
}
